/**
 * Where a refused /v1/account submit leaves the wizard.
 */

import { getStepForApiField, getFieldForStep } from '../steps.js';
import { serverErrorMessage } from '../validation/stepValidation.js';

/**
 * The step that shows the refusal. The current step wins when it owns a
 * refused field, then the earliest step that owns one. A refusal that no step
 * owns keeps the user on the current step.
 *
 * @param {string[]} fields - the refused /v1/account parameters
 * @param {number} currentStep
 * @returns {number}
 */
export function getRejectionStep(fields, currentStep) {
    const steps = fields.map(getStepForApiField).filter((step) => step > 0);

    if (steps.length === 0 || steps.includes(currentStep)) {
        return currentStep;
    }

    return Math.min(...steps);
}

/**
 * The text of the refused fields that no step shows, for the error region
 * under the steps.
 *
 * @param {Object} validationErrors - the refusal, keyed by /v1/account parameter
 * @param {string[]} shownFields - parameters with a display of their own outside the steps
 * @returns {string|null}
 */
export function getUnplacedMessage(validationErrors, shownFields) {
    const messages = Object.keys(validationErrors)
        .filter((field) => !(getStepForApiField(field) > 0) && !shownFields.includes(field))
        .map((field) => serverErrorMessage(validationErrors[field]))
        .filter(Boolean);

    return messages.length > 0 ? [...new Set(messages)].join(' ') : null;
}

/**
 * The refusal keyed as the wizard keys its errors: a step-owned parameter
 * under its step's field (city_id under currentCity), any other one as sent.
 *
 * @param {Object} validationErrors - the refusal, keyed by /v1/account parameter
 * @returns {Object}
 */
export function getStepErrors(validationErrors) {
    return Object.fromEntries(Object.entries(validationErrors).map(([field, error]) => {
        const step = getStepForApiField(field);

        return [step > 0 ? getFieldForStep(step) : field, error];
    }));
}
